import { useRef, useMemo } from 'react';

import styled from 'styled-components';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme, getColor } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import CustomScrollbar from '../../Share/Normal/CustomScrollbar';

export default function ClassAttendanceList(props) {
  const {
    frequencyData,
    calcDateText,
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const scrollRef = useRef(null);

  // 수강인원 수 내림차순 정렬 //
  const sortedFrequencyData = useMemo(() => {
    return [...frequencyData].sort((a, b) => b.reserveCount - a.reserveCount);
  }, [frequencyData]);

  // 시간 정리 //
  function calcLessonClassTime(data) {
    // 비정상 classTime(구분자 누락 등) 방어 — 한 행의 오염 데이터로 전체 목록이 깨지지 않도록 //
    if (typeof data?.classTime !== 'string' || !/^\d{1,2}:\d{2}-\d{1,2}:\d{2}$/.test(data.classTime)) {
      return (data?.classTime || '') + (data?.teacherNames ? ' : ' + data.teacherNames : '');
    }

    const tmp = data.classTime.split('-');
    const splitTmp = tmp[0].split(':');
    const splitTmp2 = tmp[1].split(':');
    let startTime = '';
    let endTime = '';

    if (parseInt(splitTmp[0]) > 12) {
      startTime = (parseInt(splitTmp[0]) - 12) + ':' + splitTmp[1] + ' PM';
    }
    else {
      startTime = splitTmp[0] + ':' + splitTmp[1] + ' AM';
    }

    if (parseInt(splitTmp2[0]) > 12) {
      endTime = (parseInt(splitTmp2[0]) - 12) + ':' + splitTmp2[1] + ' PM';
    }
    else {
      endTime = splitTmp2[0] + ':' + splitTmp2[1] + ' AM';
    }

    return startTime + ' - ' + endTime + ' : ' + data.teacherNames;
  }

  return (
    <TopLevelWrapper>
      <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} cursor='default'>
        {calcDateText() + ' ' + t('statistics.duration.classAttendance')}
      </Text>
      <ScrollArea>
        <ScrollWrapper ref={scrollRef}>
          {
            sortedFrequencyData.map((data, index) => (
              <ItemWrapper key={'ClassFrequency-' + index}>
                <TimeText fontFamily='Poppins-Medium' fontSize={14} color={theme.subText}>
                  {calcLessonClassTime(data)}
                </TimeText>
                <ItemRow>
                  <LessonInfoWrapper>
                    <ColorBar $backgroundColor={getColor(data.color, isDark)} />
                    <LessonNameText fontFamily='NotoSansKR-Medium' fontSize={16} color={theme.text}>
                      {data.lessonName}
                    </LessonNameText>
                  </LessonInfoWrapper>
                  <DivisionLine />
                  <CountBadge>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={16} color={theme.text} cursor='default'>
                      {data.reserveCount}
                    </Text>
                  </CountBadge>
                </ItemRow>
              </ItemWrapper>
            ))
          }
        </ScrollWrapper>
        <CustomScrollbar
          direction="vertical"
          scrollRef={scrollRef}
          thumbSize={67}
          thumbColor={theme.brand500}
          showTrackAlways={false}
          thumbThickness={2}
          trackColor="transparent"
        />
      </ScrollArea>
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
`;

const ScrollArea = styled.div`
  display: flex;
  flex-direction: row;

  width: 100%;
  height: 400px;

  margin-top: 20px;
`;

const ScrollWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
  height: 100%;

  gap: 20px;

  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none;
`;

const ItemWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 5px;
`;

const TimeText = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;

  height: 18px;

  white-space: nowrap;
`;

const ItemRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
  height: 40px;

  gap: 10px;
`;

const LessonInfoWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  height: 40px;

  gap: 10px;

  flex-shrink: 0;
`;

const ColorBar = styled.div`
  width: 10px;
  height: 100%;

  border-radius: 4px 0 0 4px;
  background-color: ${props => props.$backgroundColor};

  flex-shrink: 0;
`;

const LessonNameText = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;

  white-space: nowrap;
`;

const DivisionLine = styled.div`
  flex: 1;
  min-width: 1px;
  height: 0.5px;

  border-radius: 200px;
  background-color: ${props => props.theme.line};
  border: 0.5px solid ${props => props.theme.line};
`;

const CountBadge = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  height: 30px;

  padding: 0 20px;

  border: 1px solid ${props => props.theme.line};
  border-radius: 100px;

  flex-shrink: 0;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  &:hover {
    cursor: ${props => props.cursor};
  }
`;
